import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthFacade } from '../auth/auth.facade';
import { APP_HOME_URL, DEVELOPER_ALLOWED_URLS, DEVELOPER_HOME_URL, ROLE_INDEPENDENT_URLS } from '../auth/auth.state';
import { OrganizationsFacade } from '../organizations/organizations.facade';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate, CanActivateChild {
  readonly #authFacade = inject(AuthFacade);
  readonly #orgsFacade = inject(OrganizationsFacade);
  readonly #router = inject(Router);

  async canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    if (!this.#authFacade.isAuthenticated()) {
      return this.#router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // Ensure the active organization (and the user's memberships) are resolved
    // before any protected route activates — org-scoped APIs read it directly.
    await this.#orgsFacade.ensureActiveOrganization();

    return this.#enforceRoleAccess(state.url);
  }

  canActivateChild(_childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.#enforceRoleAccess(state.url);
  }

  #enforceRoleAccess(url: string): boolean | UrlTree {
    const isAllowed = DEVELOPER_ALLOWED_URLS.some(allowed => url.startsWith(allowed));
    const isRoleIndependent = ROLE_INDEPENDENT_URLS.some(u => url.startsWith(u));
    if (this.#authFacade.isDeveloper() && !isAllowed && !isRoleIndependent) {
      return this.#router.createUrlTree([DEVELOPER_HOME_URL]);
    }
    if (!this.#authFacade.permissions().canManage && url.includes('/templates/edit')) {
      return this.#router.createUrlTree([APP_HOME_URL]);
    }
    if (!this.#authFacade.permissions().canInitiateSigning && url.includes('/templates/initiate')) {
      return this.#router.createUrlTree([APP_HOME_URL]);
    }
    if (!this.#authFacade.permissions().canPreviewTemplates && url.includes('/templates/preview')) {
      return this.#router.createUrlTree([APP_HOME_URL]);
    }
    if (!this.#authFacade.permissions().canAccessApiKeys && url.startsWith(DEVELOPER_HOME_URL)) {
      return this.#router.createUrlTree([APP_HOME_URL]);
    }
    if (!this.#authFacade.isDeveloper() && !this.#authFacade.permissions().canManage && url.startsWith('/admin/settings/users')) {
      return this.#router.createUrlTree([APP_HOME_URL]);
    }
    return true;
  }
}
